import React, { useState } from 'react';

import { API } from 'constants';
import client from '../../../services/report-api';

import { useFormState } from '../../../hooks/useFormState';

import { defaultErrorMessage, emptyFieldErrorMessage } from '../../../constants/errorMessages';
import { mutateListDataAfterRenamingItem } from '../../../helpers/swrMutateUtils';
import { mergeTwoArraysRemovingDuplicates } from '../../../helpers/mergeTwoArraysRemovingDuplicates';
import { useSwrRequestKeysToMutateFromContext } from '../../../hooks/useSwrRequestKeysToMutateFromContext';

import { Button } from '../../../design-system';
import { ErrorWarningLineIcon } from '../../../design-system/Icons';
import Alert from '../../../design-system/Alert/Alert';
import Input from '../../../design-system/Input/Input';
import Modal from '../../../design-system/Modal/Modal';
import ModalHeader from '../../../design-system/ModalHeader/ModalHeader';

const RenameProjectModal = ({
    projectId,
    projectName,
    setProjects,
    mutate,
    onClose,
    requestKeysToMutate,
}) => {
    const { formData, handleInputChange, fieldErrorMessages, setFieldErrorMessages } = useFormState(
        { name: projectName }
    );

    const {
        requestKeysToMutate: { projects: requestKeysToMutateFromContext },
    } = useSwrRequestKeysToMutateFromContext();

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    const handleSubmit = async () => {
        const isNameEmpty = !formData.name;
        if (isNameEmpty) {
            setFieldErrorMessages({ name: emptyFieldErrorMessage });
            return;
        }

        try {
            setIsLoading(true);
            const { data } = await client.patch(`${API.ROUTES.report.project}${projectId}/`, {
                name: formData.name,
            });

            mutateListDataAfterRenamingItem({
                requestKeysToMutate: mergeTwoArraysRemovingDuplicates(
                    requestKeysToMutateFromContext,
                    requestKeysToMutate || []
                ),
                id: projectId,
                updatedName: data.name,
            });

            if (mutate) {
                mutate((currentData) => ({ ...currentData, name: data.name }), {
                    revalidate: false,
                });
            }

            if (setProjects) {
                setProjects((prevProjects) =>
                    prevProjects.map((project) =>
                        project.id === projectId ? { ...project, name: data.name } : project
                    )
                );
            }

            onClose();
        } catch (e) {
            setIsLoading(false);
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        }
    };

    return (
        <Modal size="medium" onClose={onClose}>
            <div className="flex flex-col w-full gap-5 lg:gap-7">
                <ModalHeader title={'Rename Project'} onClose={onClose} />
                <Input
                    name="name"
                    size="md"
                    value={formData.name}
                    label="Project Name"
                    isRequired
                    state={fieldErrorMessages.name ? 'error' : 'default'}
                    errorMessage={fieldErrorMessages.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter project name"
                />
                <div className="flex items-center justify-between pt-1">
                    <Button type="ghost" size="sm" text="Cancel" onClick={onClose} />
                    <Button
                        type="secondary"
                        size="sm"
                        text="Save"
                        state={isLoading ? 'loading' : 'default'}
                        onClick={handleSubmit}
                    />
                </div>
            </div>

            {errorAlert && (
                <Alert
                    status="critical"
                    message={errorAlert.message}
                    statusCode={errorAlert.statusCode}
                    icon={ErrorWarningLineIcon}
                    autoCloseInMS={3000}
                    handleClose={() => setErrorAlert(null)}
                />
            )}
        </Modal>
    );
};

export default RenameProjectModal;
